import {
  Button,
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import React from 'react';
import { Flex } from '@chakra-ui/react';
import { Plus } from 'lucide-react';
import { useToken } from '@chakra-ui/react';
import { IconButton } from '../Form/IconButton';
import { MultilineAsyncSelect } from '../Form/MultilineAsyncSelect';
import { PencilSVG } from '../CustomSVGs';

export type Entry = { label: string; value: string };

type Props = {
  entries: Entry[];
  errorMessage?: string;
  isEditing?: boolean;
  isSaving?: boolean;
  label: string;
  loadOptions?: (inputValue: string) => Promise<Entry[]>;
  notes?: string;
  onAddEntry?: (newEntry: Entry) => void;
  onDiscard?: () => void;
  onEditList?: () => void;
  onEntriesChange?: (entries: Entry[]) => void;
  onNotesChange?: (notes: string) => void;
  onSave?: () => void;
  showEditButton?: boolean;
};

export const ListWithNotes = ({
  entries,
  errorMessage = undefined,
  isEditing = false,
  isSaving = false,
  label,
  loadOptions = undefined,
  notes = '',
  onAddEntry = undefined,
  onDiscard = undefined,
  onEditList = undefined,
  onEntriesChange = undefined,
  onNotesChange = undefined,
  onSave = undefined,
  showEditButton = false,
}: Props) => {
  const [primary900] = useToken('colors', ['primary.900']);
  return (
    <>
      <Flex
        alignItems={isEditing ? 'center' : 'flex-start'}
        justifyContent={'space-between'}
        flexDir={'row'}
      >
        <HStack alignItems={'center'} flex={1} minH={'24px'}>
          <Text
            color={'blueGray.600'}
            fontSize={'md'}
            fontWeight={'normal'}
            textAlign="left"
          >
            {label}
          </Text>

          {isEditing && !!onAddEntry && (
            <HStack
              alignItems={'center'}
              flex={1}
              justifyContent={'space-between'}
            >
              <IconButton
                onClick={() => {
                  onAddEntry({ label: '', value: '' });
                }}
              >
                <Plus size={16} />
              </IconButton>

              <HStack>
                <Button
                  backgroundColor={'transparent'}
                  disabled={isSaving}
                  h={'20px'}
                  minH={'16px'}
                  ml={2}
                  onClick={onDiscard}
                  rounded={'full'}
                  size="xs"
                  variant={'outline'}
                  w={'16px'}
                >
                  <Text
                    color={'secondary.500'}
                    fontSize={'xs'}
                    fontWeight={'normal'}
                  >
                    Cancel
                  </Text>
                </Button>

                <Button
                  backgroundColor={'secondary.500'}
                  borderColor={'blueGray.600'}
                  borderWidth={'1px'}
                  h={'20px'}
                  loading={isSaving}
                  disabled={isSaving}
                  minH={'16px'}
                  ml={2}
                  onClick={onSave}
                  rounded={'full'}
                  size="xs"
                  w={'16px'}
                >
                  <Text color={'white'} fontSize={'xs'} fontWeight={'normal'}>
                    Save
                  </Text>
                </Button>
              </HStack>
            </HStack>
          )}

          {!isEditing && showEditButton && (
            <IconButton onClick={onEditList}>
              <PencilSVG />
            </IconButton>
          )}
        </HStack>

        {!isEditing && (
          <HStack flex={1} pt={'4px'}>
            <Text color={'secondary.200'} fontWeight={'bold'}>
              {entries.map((entry) => entry.label).join(', ')}
            </Text>
          </HStack>
        )}
      </Flex>

      {!!errorMessage && (
        <Text mt={2} fontSize={'sm'} color={'error.600'}>
          {errorMessage}
        </Text>
      )}

      {isEditing && !!onNotesChange && (
        <VStack mt={3}>
          <MultilineAsyncSelect
            entries={entries}
            loadOptions={loadOptions}
            name={'sx'}
            onEntriesChange={onEntriesChange}
            readonly={!isEditing}
          />

          <textarea
            style={{
              fontSize: 15,
              height: 80,
              width: '100%',
              maxWidth: '100%',
              marginTop: 12,
              backgroundColor: 'white',
              borderWidth: 1,
              borderStyle: 'solid',
              borderRadius: 4,
              borderColor: '#BCC7D7',
              padding: 8,
              color: primary900,
              verticalAlign: 'top',
              resize: 'none',
            }}
            onChange={(e) => {
              onNotesChange(e.target.value);
            }}
            placeholder={`${label} general notes...`}
            value={notes}
          />
        </VStack>
      )}
    </>
  );
};
